import React, { useState } from "react";
import { useRules } from "../provider/RulesContext";
import { useSelector } from "react-redux";
import CreateProjectDialog from "./CreateProjectDialogue";
import { Trash2, User, Mail, SquareArrowOutUpRight } from "lucide-react";
import { showConfirmDelete } from "../../../helpers";
import { LoadingCircle } from "../../../assets/icons";
import EmptyProjectState from "./EmptyProjectState";
import { getOverLimitChecks } from "../../../store/userSlice";
const Projects = () => {
  const { user } = useSelector((state) => {
    return state.user;
  });
  const overLimitChecks = useSelector(getOverLimitChecks);

  const { loading, projects, selectProject, delete_project } = useRules();
  const [openDialogue, setopenDialogue] = useState(false);

  const deleteRuleConfirm = (projectName, projectID) => {
    showConfirmDelete(
      projectName,
      () => {
        delete_project(projectID);
      },
      () => {
        // Do nothing on cancel
      },
    );
  };

  const canDeleteProject = (project) => {
    if (user?.role === "super_admin") return true;
    return project?.createdBy?._id === user?._id;
  };

  if (loading === "fetchingProjects") {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-lg font-semibold text-gray-600">
          Fetching Projects, please wait...
        </p>
      </div>
    );
  }

  const hasPermission =
    user.role === "super_admin" ||
    user?.permissions?.includes("Template Manager");

  return (
    <div className="p-2 lg:p-4 w-full h-full">
      {hasPermission && projects.length > 0 && (
        <div className="flex justify-end mb-4">
          <button
            className={`px-6 py-2 bg-lightBlue text-white rounded hover:bg-darkBlue transition ${overLimitChecks.mappingsExceeded && "opacity-50 cursor-not-allowed"}`}
            onClick={() => setopenDialogue(true)}
            disabled={overLimitChecks.mappingsExceeded}
          >
            Create New Project
          </button>
        </div>
      )}

      <CreateProjectDialog
        isOpen={openDialogue}
        onClose={() => setopenDialogue(false)}
      />

      {projects.length > 0 ? (
        <div className="bg-gray-50 p-2 lg:p-4 rounded-lg">
          <h2 className="text-2xl font-semibold mb-4">Projects</h2>

          <div className="max-h-[70vh] overflow-auto pr-2">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 xxl:grid-cols-4 gap-6">
              {projects.map((project) => (
                <div
                  key={project._id}
                  className="group relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-blue-200"
                >
                  {/* Header with gradient background */}
                  <div className="bg-lightBlue p-2 md:p-4 text-white relative">
                    {/* Delete button */}
                    {canDeleteProject(project) && (
                      <button
                        onClick={() => {
                          deleteRuleConfirm(project.projectName, project._id);
                        }}
                        className={`absolute top-4.5 right-3 p-1.5 rounded-full transition-colors duration-200 ${
                          loading === `deletingProject_${project._id}`
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-white/20 hover:bg-red-500"
                        }`}
                        title="Delete Project"
                      >
                        {loading === `deletingProject_${project._id}` ? (
                          LoadingCircle
                        ) : (
                          <Trash2 size={16} className="text-white" />
                        )}
                      </button>
                    )}

                    <h3 className="text-lg font-bold mb-1 pr-8 line-clamp-2">
                      {project.projectName}
                    </h3>
                  </div>

                  {/* Content */}
                  <div className="px-4  flex justify-between items-center  h-28">
                    <div className="flex-1">
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex items-start">
                          <User
                            size={14}
                            className="mr-2 mt-0.5 text-gray-400 flex-shrink-0"
                          />
                          <span className="font-medium">
                            {project?.createdBy?.firstName}{" "}
                            {project?.createdBy?.lastName}
                          </span>
                        </div>

                        <div className="flex items-start">
                          <Mail
                            size={14}
                            className="mr-2 mt-0.5 text-gray-400 flex-shrink-0"
                          />
                          <span className="break-all text-xs">
                            {project?.createdBy?.email}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action button */}
                    <button
                      className="bg-blue-800/20 hover:bg-lightBlue active:bg-darkBlue hover:text-white p-2 mx-1 rounded-lg hover:scale-110 transition-all duration-200"
                      onClick={() => {
                        selectProject(project);
                      }}
                    >
                      <SquareArrowOutUpRight />
                    </button>
                  </div>

                  {/* Decorative bottom border */}
                  {/* <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div> */}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full h-full bg-white rounded-lg shadow-md flex flex-col items-center justify-center">
          <EmptyProjectState
            setopenDialogue={setopenDialogue}
            hasPermission={hasPermission}
          />
        </div>
      )}
    </div>
  );
};

export default Projects;
